import { Stack } from '@mui/material';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithChildren,
  PropsWithoutRef,
  RefAttributes,
  useEffect,
} from 'react';
import { DefaultValues, FieldValues, FormProvider as FormProviderRHF, useForm } from 'react-hook-form';
import { SiloRealFormProvider } from './form-provider';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type Props = Required<PropsWithChildren> & {
  defaultValues?: DefaultValues<FieldValues>;
  onSubmit?: (fv: FieldValues) => void;
  onChange?: (fv: FieldValues, event: { name?: string; type?: string }) => void;
};

export const base: FR<HTMLFormElement, Props> = forwardRef((props, ref) => {
  const { children } = props;
  const { defaultValues, onSubmit, onChange } = props;

  const methods = useForm({ defaultValues });
  const { handleSubmit } = methods;

  useEffect(() => {
    if (!onChange) return;

    const unsubscribe = methods.subscribe({
      formState: { values: true },
      callback: ({ values, name, type }) => {
        onChange?.(values, { name, type });
      },
    });

    return () => unsubscribe();
  }, [methods, onChange]);

  const internalOnSubmit = handleSubmit((fv) => onSubmit?.(fv));

  return (
    <SiloRealFormProvider>
      <FormProviderRHF {...methods}>
        <Stack ref={ref} component="form" gap={2} onSubmit={internalOnSubmit} pt={0.5}>
          {children}
        </Stack>
      </FormProviderRHF>
    </SiloRealFormProvider>
  );
});

base.displayName = 'SiloReal-Form';
